import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import { setFullScreen } from '@store/actionSlices/appConfig'
import {
  ExtendedUnitFilterOptions,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import usePropertyCompareFirebaseTrigger from '@components/compare-property/utils'
import Container from '@components/container/v2'
import { Status } from '@components/data-handler/data-handler'
import DataHandler from '@components/data-handler/v2'
import BuildingFilter from '@components/filter/v2/building-filter'
import FILTER_INITIAL_STATE from '@components/filter/v2/filterState'
import PropertyCompareTransitionWrapper from '@components/properties-view/v2'
import { PannellumDataInterface } from '@components/showcase-pannellum/types'

import FloorplateView from '@pages/properties-view/apartments/v2/floorplate-view'
import UnitView from '@pages/properties-view/apartments/v2/unit-view'
import UnitsSidePanel from '@pages/properties-view/apartments/v2/units-side-panel'

import {
  selectFromResult as selectFromBuildingResult,
  useGetBuildingQuery,
} from '@api/building'
import {
  InteractivePlanData,
  selectFromResult as selectFromInteractivePlanResult,
  useGetInteractivePlanQuery,
} from '@api/interactive-plan'
import {
  selectFromResult as selectFromPanoramicResult,
  useGetPanoramicQuery,
} from '@api/panoramic'

import { SessionMap } from '@firebaseUtil/types'

import {
  useCompareProperty,
  useSyncComparablePropertiesFromSession,
} from '@adUtilities/components/compare-property/utils'
import { getQueryStringParams } from '@adUtilities/helper'
import { PropertyType, Unit } from '@adUtilities/types'

import { BuildingSkeleton, FloorplateSkeleton } from './skeleton'

export interface BuildingProps {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
}

function Building({ session, projectIdentity }: BuildingProps) {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const urlParams = useRef(getQueryStringParams(location.search))

  const [unit, setUnit] = useState<Unit>()
  const [interactivePlan, setInteractivePlan] = useState<InteractivePlanData>({
    areaView: {
      image: '',
      polygons: [],
    },
    blocks: {},
    floorplan: {},
    precinct: {},
    stages: {},
  })

  const [panoramic, setPanoramic] = useState<PannellumDataInterface[]>([])

  const [activeBlock, setActiveBlock] = useState<string>('')
  const [activeLevel, setActiveLevel] = useState<string>('')
  const [activeUnit, setActiveUnit] = useState<string>('')

  const [unitFilter, setUnitFilter] =
    useState<ExtendedUnitFilterOptions>(FILTER_INITIAL_STATE)

  const [fullScreenToggle, setFullScreenToggle] = useState<boolean>(
    projectIdentity?.fullScreen
  )
  const [unitSidePanelState, setUnitSidePanelState] = useState<boolean>(
    !projectIdentity?.fullScreen
  )
  const [isFilterOpen, toggleFilter] = useState<boolean>(false)

  const interactivePlanPayload = useGetInteractivePlanQuery(
    { projectName: projectIdentity.projectName },
    { selectFromResult: selectFromInteractivePlanResult }
  )

  const buildingPayload = useGetBuildingQuery(
    { projectName: projectIdentity.projectName },
    { selectFromResult: selectFromBuildingResult }
  )

  const panoramicPayload = useGetPanoramicQuery(
    { projectName: projectIdentity.projectName },
    { selectFromResult: selectFromPanoramicResult }
  )

  const floorplanLength = useMemo(
    () => Object.keys(interactivePlan.floorplan).length,
    [interactivePlan.floorplan]
  )
  const building = useMemo(() => {
    const { levels = [], blockOrders = [] } = buildingPayload || {}

    return { levels, blockOrders }
  }, [buildingPayload])

  const handleFullScreenToggle = (arg: boolean) => {
    setFullScreenToggle(arg)
    setUnitSidePanelState(!arg)
    dispatch(setFullScreen(arg))
  }

  const findUnit = () => {
    building.levels.forEach((level) => {
      const foundUnit = level.data.find(
        (unt) => unt.name.replace(/\s/g, '') === activeUnit.replace(/\s/g, '')
      )
      if (foundUnit) {
        setUnit(foundUnit)
      }
    })
  }

  const resetUnit = () => {
    setUnit(undefined)
  }

  useEffect(() => {
    if (building.levels.length && activeUnit) {
      findUnit()
    }
    if (!activeUnit) {
      resetUnit()
    }
  }, [building, activeUnit])

  useEffect(() => {
    const { maps } = interactivePlanPayload
    if (!floorplanLength && Object.keys(maps.floorplan || {}).length) {
      setInteractivePlan(maps)
    }
  }, [interactivePlanPayload, interactivePlan.floorplan])

  useEffect(() => {
    const { panoramicData, isLoaded } = panoramicPayload
    if (panoramic.length === 0 && isLoaded && panoramicData.length > 0) {
      setPanoramic(panoramicData)
    }
  }, [panoramicPayload])

  useEffect(() => {
    if (
      !(
        urlParams.current?.block ||
        urlParams.current?.level ||
        urlParams.current?.unit
      )
    ) {
      return
    }
    history.replace(location.pathname)
  }, [location.pathname])

  useEffect(() => {
    if (!session) {
      return
    }

    const {
      building: { activeBlock, activeLevel, activeUnit, unitFilter },
    } = session

    setActiveBlock(activeBlock)
    setActiveLevel(activeLevel)
    setActiveUnit(activeUnit)

    setUnitFilter(unitFilter)
  }, [session])

  const comparePropertyProps = useCompareProperty()

  const { triggerComparisonStop } = usePropertyCompareFirebaseTrigger({
    comparePropertyProps,
  })

  useSyncComparablePropertiesFromSession({
    disable: !session?.connected,
    firebasePropertyCompare: session?.propertyCompare,
    dataLoaded: buildingPayload.status === Status.FULFILLED,
    data: buildingPayload.levels,
    comparePropertyProps,
    propertyType: PropertyType.Unit,
  })

  useEffect(() => {
    if (projectIdentity?.fullScreen) {
      dispatch(setFullScreen(true))
    }
  }, [projectIdentity?.fullScreen])

  React.useEffect(() => {
    const unsubscribeHistory = history.listen(() => triggerComparisonStop())
    return () => unsubscribeHistory()
  }, [history])

  const handleVideoPlayerState = React.useCallback(
    (status: boolean) => {
      if (!status && projectIdentity.fullScreen) {
        return
      }
      handleFullScreenToggle(status)
    },
    [handleFullScreenToggle, projectIdentity.fullScreen]
  )

  return (
    <Container className="flex" fullScreenMode={fullScreenToggle}>
      <DataHandler
        payload={{
          ...buildingPayload,
          data: building.levels,
        }}
        skeletonFrame={
          <BuildingSkeleton fullscreen={projectIdentity.fullScreen} />
        }
        className="flex"
      >
        <div
          className={`h-full ${
            !unitSidePanelState ? '!w-0 opacity-0' : 'opacity-100'
          } w-[297px] flex-grow-0 transition-all duration-300 ease-in-out`}
        >
          <UnitsSidePanel
            building={building}
            blocks={interactivePlan.blocks}
            unitFilter={unitFilter}
            filterPopupToggle={isFilterOpen}
            setFilterPopupToggle={(arg: boolean) => {
              toggleFilter(arg)
            }}
            firebaseActiveBlock={activeBlock}
            firebaseActiveLevel={activeLevel}
            firebaseActiveUnit={activeUnit}
            comparePropertyProps={comparePropertyProps}
          />
          <BuildingFilter
            isOpen={isFilterOpen}
            building={building}
            firebaseUnitFilter={unitFilter}
            firebaseActiveBlock={activeBlock}
            firebaseActiveLevel={activeLevel}
            toggle={toggleFilter}
          />
        </div>

        <PropertyCompareTransitionWrapper
          propertyType={PropertyType.Unit}
          comparePropertyProps={comparePropertyProps}
          fullScreenMode={fullScreenToggle}
          onFullScreenToggle={handleFullScreenToggle}
          triggerComparisonStop={triggerComparisonStop}
        >
          <div
            className={`z-10 h-full flex-1 ${
              activeUnit ? 'transition-all duration-300 ease-in-out' : ''
            }`}
          >
            {activeUnit && unit && (
              <UnitView
                key={unit.id}
                unit={unit}
                panoramic={panoramic}
                fullScreenToggle={fullScreenToggle}
                setFullScreenToggle={handleFullScreenToggle}
                getVideoPlayerState={handleVideoPlayerState}
                comparePropertyProps={comparePropertyProps}
              />
            )}

            {activeLevel && !activeUnit && (
              <DataHandler
                payload={{
                  ...interactivePlanPayload,
                  data: floorplanLength,
                }}
                skeletonFrame={<FloorplateSkeleton />}
                className="relative"
              >
                <FloorplateView
                  firebaseActiveBlock={activeBlock}
                  firebaseActiveLevel={activeLevel}
                  building={building}
                  floorplan={interactivePlan.floorplan}
                  blocks={interactivePlan.blocks}
                  unitFilter={unitFilter}
                  panoramic={panoramic}
                  fullScreenToggle={fullScreenToggle}
                  setFullScreenToggle={handleFullScreenToggle}
                  getVideoPlayerState={handleVideoPlayerState}
                />
              </DataHandler>
            )}
          </div>
        </PropertyCompareTransitionWrapper>
      </DataHandler>
    </Container>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
  })
)(Building)
